import { useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./FeedbackBlock.module.scss";

const FeedbackBlock = () => {
  const { t } = useTranslation("common");
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const ratingData = {
    [t("feedback.excellent")]: 100,
    [t("feedback.veryGood")]: 0,
    [t("feedback.average")]: 0,
    [t("feedback.poor")]: 0,
    [t("feedback.veryPoor")]: 0,
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: { [key: string]: string } = {};

    if (rating === 0) newErrors.rating = t("feedback.errors.selectRating");
    if (feedback.trim() === "")
      newErrors.feedback = t("feedback.errors.feedbackRequired");
    if (name.trim() === "") newErrors.name = t("feedback.errors.nameRequired");
    if (email.trim() === "")
      newErrors.email = t("feedback.errors.emailRequired");
    else if (!/\S+@\S+\.\S+/.test(email))
      newErrors.email = t("feedback.errors.emailInvalid");

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
    } else {
      setIsLoading(true);
      setSuccessMessage("");

      setTimeout(() => {
        setIsLoading(false);
        setSuccessMessage(t("feedback.successMessage"));

        setRating(0);
        setFeedback("");
        setName("");
        setEmail("");
        setErrors({});
      }, 2000);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: string
  ) => {
    const { value } = e.target;
    if (value.trim() !== "") {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: "",
      }));
    }
  };

  return (
    <div className="container section">
      <div className="sectionContainer sectionContainerWhite platformCont">
        <div className={styles.formAndStats}>
          <div className={styles.leftBlock}>
            <div className={styles.feedbackHeader}>
              <p className={styles.inputLabel}>{t("feedback.rating")}</p>
              <div className={styles.rating}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <img
                    key={star}
                    src={
                      star <= rating
                        ? "/images/star-full.svg"
                        : "/images/star-empty.svg"
                    }
                    alt={t("feedback.starAlt", { number: star })}
                    className={styles.star}
                    onClick={() => setRating(star)}
                  />
                ))}
              </div>
              {errors.rating && <p className={styles.error}>{errors.rating}</p>}
            </div>
            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.inputGroup}>
                <p className={styles.inputLabel}>
                  {t("feedback.experienceQuestion")}
                </p>
                <textarea
                  placeholder={t("feedback.shareFeedback")}
                  value={feedback}
                  onChange={(e) => {
                    setFeedback(e.target.value);
                    handleInputChange(e, "feedback");
                  }}
                  className={styles.textarea}
                />
                {errors.feedback && (
                  <p className={styles.error}>{errors.feedback}</p>
                )}
              </div>

              <div className={styles.inputGroup}>
                <p className={styles.inputLabel}>{t("feedback.name")}</p>
                <input
                  type="text"
                  placeholder={t("feedback.namePlaceholder")}
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    handleInputChange(e, "name");
                  }}
                  className={styles.input}
                />
                {errors.name && <p className={styles.error}>{errors.name}</p>}
              </div>

              <div className={styles.inputGroup}>
                <p className={styles.inputLabel}>{t("feedback.emailLabel")}</p>
                <input
                  type="email"
                  placeholder={t("feedback.emailPlaceholder")}
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    handleInputChange(e, "email");
                  }}
                  className={styles.input}
                />
                {errors.email && <p className={styles.error}>{errors.email}</p>}
              </div>

              <button
                type="submit"
                className={`${styles.submitBtn} ${
                  isLoading ? styles.loading : ""
                }`}
              >
                {isLoading ? t("feedback.submitting") : t("feedback.submit")}
              </button>
            </form>
          </div>
          <div className={styles.ratingSummary}>
            <div className={styles.ratingBarContainer}>
              <div className={styles.ratingLine}>
                <span className={styles.ratingText}>5,0</span>
                <div className={styles.ratingStars}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <img
                      key={star}
                      src="/images/star-full.svg"
                      alt={t("feedback.fullStarAlt", { number: star })}
                      className={styles.star}
                    />
                  ))}
                </div>
              </div>

              <p className={styles.statsSubtitle}>
                {t("feedback.ratingSummary", { rating: "5,0", total: 5 })}
              </p>
              {Object.keys(ratingData).map((key) => (
                <div key={key} className={styles.ratingCategory}>
                  <div className={styles.ratingCategoryText}>
                    <span>{key}</span>
                  </div>
                  <div className={styles.ratingBarWrapper}>
                    <div className={styles.ratingBar}>
                      <div
                        className={styles.filled}
                        style={{ width: `${ratingData[key]}%` }}
                      />
                    </div>
                  </div>
                  <span className={styles.percentageText}>
                    {ratingData[key]}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
        {successMessage && (
          <div className={styles.successMessage}>{successMessage}</div>
        )}
      </div>
    </div>
  );
};

export default FeedbackBlock;
